/**
 * LT-Estimate-UX-01 Section 5 — override review panel.
 *
 * Non-blocking collapsed summary of every library line that has diverged
 * from its recipe_snapshot on this tender. Three options per line:
 *   (a) Keep as-is — job-specific (DEFAULT). Writes a "__decision_keep__"
 *       event so future analytics can distinguish decided-keep from
 *       not-yet-reviewed.
 *   (b) Update the library item — reuses POST /api/library/{id}/push-from-tender
 *       via the existing UpdateLibraryFromLineDialog. Guardrails:
 *         • Side-by-side old vs new (already provided by dialog).
 *         • Blast radius: N other draft tenders currently link this item.
 *         • Never alters sent tenders (endpoint refuses on archived items).
 *         • Written to audit_log via the endpoint.
 *   (c) Save as a NEW library item — reuses SaveToLibraryModal.
 *
 * Description edits appear as a separate, quieter category (grey), not
 * as a pricing override.
 */
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  BookPlus, ChevronDown, ChevronRight, Check, RotateCcw, AlertTriangle, X,
} from "lucide-react";
import { api } from "../lib/api";
import { gbp } from "../lib/format";

// Human-readable field labels for the pricing diff set. Falls back to the raw
// field key for anything we haven't seen yet (defensive).
const FIELD_LABELS = {
  hours:         "Hours",
  hourly_rate:   "\u00a3/hr",
  material_rate: "Material rate",
  plant_rate:    "Plant rate",
  subby_rate:    "Subby rate",
  unit:          "Unit",
};

const cls = (...xs) => xs.filter(Boolean).join(" ");

export function OverrideReviewPanel({
  tenderId,
  onOpenLine,             // (lineId) => void   — jumps user to Estimate row
  onOpenLibraryUpdate,    // (line) => void     — opens UpdateLibraryFromLineDialog
  onOpenSaveAsNew,        // (line) => void     — opens SaveToLibraryModal
}) {
  const [expanded, setExpanded] = useState(true);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // decisions: local per-line "kept" flag so the UI can show a tick without
  // reloading. Persisted to the server via /keep endpoint.
  const [kept, setKept] = useState({});
  // blast radius per library item (how many OTHER draft tenders currently
  // link this same library_item_id). Fetched lazily on first render.
  const [blast, setBlast] = useState({});

  const load = () => {
    setLoading(true);
    api.get(`/tenders/${tenderId}/override-events`)
      .then((r) => setData(r.data))
      .catch(() => toast.error("Failed to load override events"))
      .finally(() => setLoading(false));
  };
  useEffect(load, [tenderId]); // eslint-disable-line

  const pricingLines = useMemo(
    () => (data?.lines || []).filter((l) => (l.pricing_fields || []).length > 0),
    [data]
  );
  const descriptionOnlyLines = useMemo(
    () => (data?.lines || []).filter((l) => (l.pricing_fields || []).length === 0 && l.description_edited),
    [data]
  );

  // Fetch blast radius per library item — one small call per unique lib id.
  useEffect(() => {
    const libIds = Array.from(new Set(pricingLines.map((l) => l.library_item_id).filter(Boolean)));
    for (const lid of libIds) {
      if (blast[lid] != null) continue;
      api.get(`/library/${lid}/usage-count`)
        .then((r) => setBlast((prev) => ({ ...prev, [lid]: r.data?.draft_tender_count ?? 0 })))
        .catch(() => setBlast((prev) => ({ ...prev, [lid]: null })));
    }
  }, [pricingLines]); // eslint-disable-line

  const doKeep = async (lineId) => {
    try {
      await api.post(`/tenders/${tenderId}/override-events/keep`, { line_id: lineId });
      setKept((prev) => ({ ...prev, [lineId]: true }));
      toast.success("Override kept as job-specific");
    } catch (_) {
      toast.error("Failed to record decision");
    }
  };

  const total = pricingLines.length + descriptionOnlyLines.length;

  if (loading) {
    return (
      <div
        data-testid="override-review-panel-loading"
        className="rounded-md border border-line bg-surface px-4 py-3 text-ink-3"
        style={{ fontSize: 12 }}
      >
        Loading override review…
      </div>
    );
  }

  if (total === 0) {
    return (
      <div
        data-testid="override-review-panel-empty"
        className="rounded-md border border-line bg-surface-2/60 px-4 py-3 text-ink-3 inline-flex items-center gap-2"
        style={{ fontSize: 12 }}
      >
        <Check size={13} className="text-accent" />
        No library items have been modified on this tender.
      </div>
    );
  }

  return (
    <section
      data-testid="override-review-panel"
      className="rounded-md border border-warning-soft bg-surface"
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-surface-2/40"
      >
        <div className="flex items-center gap-2 text-ink" style={{ fontSize: 13 }}>
          <AlertTriangle size={14} className="text-warning" />
          <span style={{ fontWeight: 600 }}>
            {pricingLines.length} library {pricingLines.length === 1 ? "item was" : "items were"} modified on this tender
          </span>
          {descriptionOnlyLines.length > 0 && (
            <span className="text-ink-3" style={{ fontSize: 12 }}>
              · {descriptionOnlyLines.length} description edit{descriptionOnlyLines.length === 1 ? "" : "s"}
            </span>
          )}
        </div>
        {expanded ? <ChevronDown size={14} className="text-ink-3" /> : <ChevronRight size={14} className="text-ink-3" />}
      </button>

      {expanded && (
        <div className="border-t border-line-soft">
          {pricingLines.length > 0 && (
            <div className="px-4 py-3 border-b border-line-soft">
              <div className="col-head mb-2">Pricing overrides — review each one</div>
              <ul className="space-y-2">
                {pricingLines.map((l) => (
                  <li key={l.line_id}
                      data-testid={`override-review-line-${l.line_id}`}
                      className="rounded-md border border-line-soft bg-surface-2/40 p-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <button
                          type="button"
                          onClick={() => onOpenLine?.(l.line_id)}
                          className="text-ink hover:underline text-left"
                          style={{ fontSize: 13, fontWeight: 500 }}
                        >
                          {l.line_description || "(no description)"}
                        </button>
                        {blast[l.library_item_id] != null && (
                          <div className="text-ink-3 mt-0.5" style={{ fontSize: 11 }}>
                            Blast radius: {blast[l.library_item_id]} other draft tender{blast[l.library_item_id] === 1 ? "" : "s"} link this library item.
                            Sent tenders are never re-priced.
                          </div>
                        )}
                        <ul className="mt-2 space-y-1">
                          {l.pricing_fields.map((pf, i) => (
                            <li key={i} className="flex items-baseline justify-between gap-3"
                                style={{ fontSize: 12 }}>
                              <span className="text-ink-2">{FIELD_LABELS[pf.field] || pf.field}</span>
                              <span className="font-mono tnum">
                                <span className="text-ink-3">{pf.library_value ?? "\u2014"}</span>
                                <span className="mx-1 text-ink-4">\u2192</span>
                                <span className="text-warning" style={{ fontWeight: 600 }}>
                                  {pf.entered_value ?? "\u2014"}
                                </span>
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div className="flex flex-col gap-1.5 flex-shrink-0" style={{ minWidth: 200 }}>
                        <button
                          type="button"
                          onClick={() => doKeep(l.line_id)}
                          disabled={!!kept[l.line_id]}
                          data-testid={`override-review-keep-${l.line_id}`}
                          className={cls(
                            "rounded-md px-3 py-1.5 border text-left inline-flex items-center gap-2 transition-colors",
                            kept[l.line_id]
                              ? "border-accent bg-accent-soft text-accent"
                              : "border-line bg-surface hover:bg-surface-2 text-ink-2"
                          )}
                          style={{ fontSize: 12 }}
                        >
                          {kept[l.line_id] ? <Check size={12} /> : <RotateCcw size={12} />}
                          {kept[l.line_id] ? "Kept as job-specific" : "Keep as-is (job-specific)"}
                        </button>
                        <button
                          type="button"
                          onClick={() => onOpenLibraryUpdate?.(l)}
                          disabled={!l.library_item_id}
                          data-testid={`override-review-update-lib-${l.line_id}`}
                          className="rounded-md px-3 py-1.5 border border-warning bg-warning-soft hover:bg-warning-soft/80 text-warning-strong inline-flex items-center gap-2 disabled:opacity-40"
                          style={{ fontSize: 12, fontWeight: 500 }}
                        >
                          <BookPlus size={12} />
                          Update library item
                        </button>
                        <button
                          type="button"
                          onClick={() => onOpenSaveAsNew?.(l)}
                          data-testid={`override-review-save-new-${l.line_id}`}
                          className="rounded-md px-3 py-1.5 border border-line bg-surface hover:bg-surface-2 text-ink-2 inline-flex items-center gap-2"
                          style={{ fontSize: 12 }}
                        >
                          <BookPlus size={12} className="text-accent" />
                          Save as new library item
                        </button>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {descriptionOnlyLines.length > 0 && (
            <div className="px-4 py-3">
              <div className="col-head mb-2">Description edits (non-pricing)</div>
              <ul className="space-y-1.5">
                {descriptionOnlyLines.map((l) => (
                  <li key={l.line_id}
                      data-testid={`override-review-desc-only-${l.line_id}`}
                      className="flex items-center justify-between gap-3">
                    <button
                      type="button"
                      onClick={() => onOpenLine?.(l.line_id)}
                      className="text-ink-2 hover:underline text-left"
                      style={{ fontSize: 12.5 }}
                    >
                      <span className="inline-block rounded-full mr-2" style={{ width: 5, height: 5, background: "#94A3B8", verticalAlign: "middle" }} />
                      {l.line_description || "(no description)"}
                    </button>
                    <span className="text-ink-3" style={{ fontSize: 11 }}>edited</span>
                  </li>
                ))}
              </ul>
              <div className="text-ink-3 mt-2" style={{ fontSize: 11 }}>
                Description edits are tracked but never treated as pricing overrides.
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
